<script setup lang="ts">
import { TransitionVertical } from '@proj-airi/ui'
import { ref } from 'vue'

const { content, metadata } = defineProps<{
  content: string
  metadata?: {
    type?: string
    callType?: string
    duration?: number
    startTime?: number
    mode?: string
    icon?: string
    recalledContent?: string
    recalledAt?: number
    details?: string
    eventKind?: string
  }
}>()

const emit = defineEmits<{
  (e: 'navigate', path: string): void
  (e: 'dismiss'): void
}>()

const isExpanded = ref(false)

function formatRecallTime(timestamp: number): string {
  const date = new Date(timestamp)
  return date.toLocaleString('zh-CN', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
</script>

<template>
  <div
    my-1 w-full flex="~ col" items-center
  >
    <div

      bg="gray-200/70 dark:gray-700/70"
      text="gray-600 dark:gray-300"

      rounded-full px-3 py-1 text-center text-xs
      max-w="80%"
    >
      <!-- 通话结束消息显示图标 + 查看详情入口 -->
      <div v-if="metadata?.type === 'call_end'" flex items-center justify-center gap-1.5>
        <div
          :class="metadata.callType === 'video' ? 'i-carbon:video' : 'i-carbon:phone'"
          h-3 w-3
        />
        <span>{{ content }}</span>
        <button class="call-log-link" aria-label="查看通话记录详情" @click.stop="emit('navigate', '/call-logs')">
          查看详情 &gt;
        </button>
      </div>
      <!-- 线下/线上模式切换消息 -->
      <div v-else-if="metadata?.type === 'offline_switch'" flex items-center justify-center gap-1.5>
        <div
          :class="metadata.mode === 'offline' ? 'i-solar:map-point-bold-duotone' : 'i-solar:map-point-line-duotone'"
          h-3 w-3
          :text="metadata.mode === 'offline' ? 'primary-500 dark:primary-400' : 'primary-500 dark:primary-400'"
        />
        <span>{{ content }}</span>
      </div>
      <!-- 插件系统通知（如余额不足） -->
      <div v-else-if="metadata?.type === 'plugin_notification'" flex items-center justify-center gap-1.5>
        <div
          :class="metadata.icon || 'i-solar:wallet-bold-duotone'"
          h-3 w-3
        />
        <span>{{ content }}</span>
        <button
          v-if="metadata.details"
          class="details-toggle"
          :aria-expanded="isExpanded"
          @click.stop="isExpanded = !isExpanded"
        >
          {{ isExpanded ? '收起' : '展开详情' }}
        </button>
        <button
          class="details-toggle"
          aria-label="关闭通知"
          @click.stop="emit('dismiss')"
        >
          关闭
        </button>
      </div>
      <!-- 撤回消息通知 -->
      <div v-else-if="metadata?.type === 'message_recalled'" flex items-center justify-center gap-1.5>
        <div i-solar:undo-left-round-bold-duotone h-3 w-3 op-70 />
        <button
          class="recall-toggle"
          @click.stop="isExpanded = !isExpanded"
        >
          {{ content }}
        </button>
      </div>
      <!-- 关系变化通知 -->
      <div v-else-if="metadata?.type === 'relationship_change'" flex items-center justify-center gap-1.5>
        <div i-solar:users-group-rounded-bold-duotone h-3 w-3 />
        <span>{{ content }}</span>
      </div>
      <!-- 拍一拍消息 -->
      <div v-else-if="metadata?.eventKind === 'pat'" flex items-center justify-center gap-1.5>
        <div i-solar:hand-shake-bold-duotone h-3.5 w-3.5 />
        <span>{{ content }}</span>
      </div>
      <!-- 普通系统消息 -->
      <span v-else>{{ content }}</span>
    </div>
    <!-- plugin_notification 展开详情 -->
    <TransitionVertical>
      <div
        v-if="isExpanded && metadata?.type === 'plugin_notification' && metadata?.details"
        mt-1 max-w="80%"
        bg="gray-100/60 dark:gray-800/60"
        rounded-lg px-3 py-2
        text="xs gray-500 dark:gray-400 left"
        style="word-break: break-all; white-space: pre-wrap;"
      >
        {{ metadata.details }}
      </div>
    </TransitionVertical>
    <!-- 撤回消息展开内容 -->
    <TransitionVertical>
      <div
        v-if="isExpanded && metadata?.type === 'message_recalled' && metadata?.recalledContent"
        mt-1 max-w="80%"
        bg="gray-100/60 dark:gray-800/60"
        rounded-lg px-3 py-2
        text="xs gray-500 dark:gray-400 center"
      >
        <div mb-1 op-60>
          撤回于 {{ formatRecallTime(metadata.recalledAt || 0) }}
        </div>
        <div text="gray-600 dark:gray-300">
          {{ metadata.recalledContent }}
        </div>
      </div>
    </TransitionVertical>
  </div>
</template>

<style scoped>
.recall-toggle {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  text-decoration: underline dotted;
  text-underline-offset: 2px;
  padding: 0;
  font: inherit;
}

.recall-toggle:hover {
  opacity: 0.7;
}

.call-log-link {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  opacity: 0.6;
  padding: 0;
  font: inherit;
  margin-left: 2px;
  text-decoration: underline dotted;
  text-underline-offset: 2px;
}

.call-log-link:hover {
  opacity: 1;
}

.details-toggle {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  opacity: 0.6;
  padding: 0;
  font: inherit;
  margin-left: 2px;
  text-decoration: underline dotted;
  text-underline-offset: 2px;
}

.details-toggle:hover {
  opacity: 1;
}
</style>
